export const queryKeys = {
  businessPartnerItems: (businessPartnerId: number) => ['businessPartnerItems', businessPartnerId] as const,
  businessPartnerAddresses: (businessPartnerId: number) => ['businessPartnerAddresses', businessPartnerId] as const,
  businessPartnerContactEmployees: (businessPartnerId: number) => ['businessPartnerContactEmployees', businessPartnerId] as const,
  syncRuns: ['syncRuns'] as const,
  syncRunsLatest: ['syncRuns', 'latest'] as const,
  supplierPriceLists: {
    all: ['supplierPriceLists'] as const,
    list: (filters?: unknown) => ['supplierPriceLists', 'list', filters] as const,
    detail: (id: string) => ['supplierPriceLists', 'detail', id] as const,
    runs: (id: string, filters?: unknown) =>
      ['supplierPriceLists', 'detail', id, 'runs', filters] as const,
    items: (id: string, filters?: unknown) =>
      ['supplierPriceLists', 'detail', id, 'items', filters] as const,
    itemHistory: (id: string, itemId: string, filters?: unknown) =>
      [
        'supplierPriceLists',
        'detail',
        id,
        'items',
        itemId,
        'history',
        filters,
      ] as const,
  },
  preisTool: {
    all: ['preisTool'] as const,
    itemsAll: () => ['preisTool', 'items'] as const,
    items: (filters?: unknown) => ['preisTool', 'items', filters] as const,
    facets: () => ['preisTool', 'facets'] as const,
    itemDetail: (itemId: string) =>
      ['preisTool', 'itemDetail', itemId] as const,
    supplierOffers: (itemId: string) =>
      ['preisTool', 'supplierOffers', itemId] as const,
    supplierAnomalies: (filters?: unknown) =>
      ['preisTool', 'supplierAnomalies', filters] as const,
    priceChangeBatch: (batchId: string) =>
      ['preisTool', 'priceChangeBatch', batchId] as const,
    supplierOffersAll: () => ['preisTool', 'supplierOffers'] as const,
    supplierAnomaliesAll: () => ['preisTool', 'supplierAnomalies'] as const,
    legendThresholds: () => ['preisTool', 'legendThresholds'] as const,
    priceListColumnNames: () =>
      ['preisTool', 'priceListColumnNames'] as const,
    suppliers: () => ['preisTool', 'suppliers'] as const,
    upstreamSupplierNames: () =>
      ['preisTool', 'upstreamSupplierNames'] as const,
    fixedPricesAll: () => ['preisTool', 'fixedPrices'] as const,
    fixedPrices: (itemId: string) =>
      ['preisTool', 'fixedPrices', itemId] as const,
    dispositionOfItem: (itemId: string) =>
      ['preisTool', 'disposition', itemId] as const,
    /** `null` = no target margin chosen, the set default applies. */
    disposition: (itemId: string, targetMargin: number | null) =>
      ['preisTool', 'disposition', itemId, targetMargin] as const,
    salesHistory: (itemId: string) =>
      ['preisTool', 'salesHistory', itemId] as const,
    // Purchasing aid settings (EOS-62)
    dispositionAll: () => ['preisTool', 'disposition'] as const,
    salesHistoryAll: () => ['preisTool', 'salesHistory'] as const,
    dispositionSettings: () => ['preisTool', 'dispositionSettings'] as const,
    priceHistoryAll: () => ['preisTool', 'priceHistory'] as const,
    priceHistory: (itemId: string, days: number) =>
      ['preisTool', 'priceHistory', itemId, days] as const,
    priceWritebackStatus: () => ['preisTool', 'priceWritebackStatus'] as const,
  },
  emailSubscriptions: {
    byContact: (contactEmployeeId: number) =>
      ['emailSubscriptions', 'contact', contactEmployeeId] as const,
    mine: ['emailSubscriptions', 'mine'] as const,
  },
  roles: {
    all: ['roles'] as const,
    detail: (id: string) => ['roles', id] as const,
    permissionCatalog: ['roles', 'permission-catalog'] as const,
  },
  users: {
    all: ['users'] as const,
    detail: (id: string) => ['users', id] as const,
  },
  contactLists: {
    all: ['contactLists'] as const,
  },
  dashboard: {
    metrics: (params?: unknown) => ['dashboard', 'metrics', params] as const,
    periodMetrics: (params?: unknown) =>
      ['dashboard', 'period-metrics', params] as const,
    attention: ['dashboard', 'attention'] as const,
    revenueChart: (params?: unknown) =>
      ['dashboard', 'revenue-chart', params] as const,
    topItems: (params?: unknown) =>
      ['dashboard', 'top-items', params] as const,
    topCustomers: (params?: unknown) =>
      ['dashboard', 'top-customers', params] as const,
    systemHealth: ['dashboard', 'system-health'] as const,
    orderSources: (params?: unknown) =>
      ['dashboard', 'order-sources', params] as const,
    aovTrend: (params?: unknown) =>
      ['dashboard', 'aov-trend', params] as const,
    customerConcentration: (params?: unknown) =>
      ['dashboard', 'customer-concentration', params] as const,
    monthlyComparison: (params?: unknown) =>
      ['dashboard', 'monthly-comparison', params] as const,
    topManufacturers: (params?: unknown) =>
      ['dashboard', 'top-manufacturers', params] as const,
    recentOrders: (params?: unknown) =>
      ['dashboard', 'recent-orders', params] as const,
  },
  emailBuilder: {
    render: (payload?: unknown) => ['emailBuilder', 'render', payload] as const,
    sends: ['emailBuilder', 'sends'] as const,
    sendDetail: (sendId: number) =>
      ['emailBuilder', 'sends', sendId] as const,
    recipientConsent: ['emailBuilder', 'recipient-consent'] as const,
  },
  specifications: {
    all: ['specifications'] as const,
    queue: (filters?: unknown) =>
      ['specifications', 'queue', filters] as const,
    extractionJobs: (status?: string) =>
      ['specifications', 'jobs', status] as const,
    pickerProductLines: (params?: unknown) =>
      ['specifications', 'picker', 'productLines', params] as const,
    pickerModels: (params?: unknown) =>
      ['specifications', 'picker', 'models', params] as const,
    pickerVocab: (params?: unknown) =>
      ['specifications', 'picker', 'vocab', params] as const,
  },
  aiConversations: {
    all: ['aiConversations'] as const,
    detail: (id: string) => ['aiConversations', id] as const,
  },
  appState: ['appState'] as const,
  announcements: {
    all: ['announcements'] as const,
    maintenance: ['announcements', 'maintenance'] as const,
  },
  sourcing: {
    all: ['sourcing'] as const,
    list: (filters?: unknown) => ['sourcing', 'list', filters] as const,
    detail: (id: string) => ['sourcing', 'detail', id] as const,
    offers: (id: string) => ['sourcing', 'offers', id] as const,
    notifications: (id: string) =>
      ['sourcing', 'notifications', id] as const,
    itemCandidates: (q: string) =>
      ['sourcing', 'item-candidates', q] as const,
    supplierCandidates: (q: string) =>
      ['sourcing', 'supplier-candidates', q] as const,
    supplierContacts: (id: number) =>
      ['sourcing', 'supplier-contacts', id] as const,
    portal: (token: string) => ['sourcing', 'portal', token] as const,
    statusCounts: () => ['sourcing', 'status-counts'] as const,
  },
} as const;
